import type { MouseEventHandler } from 'react';
import styled, { useTheme, type DefaultTheme } from 'styled-components';

import { Box, type BoxProps } from '../Core';
import { Icon, IconName } from '../Icons';
import type { IconName as IconNameType } from '../Icons/IconNames';

import { IndicatorDot, IndicatorDotVariants } from '../IndicatorDot';
import { NotificationWrapper } from './styles';
import type { NotificationSizes } from './types';
import { NotificationVariants } from './types';

export interface InternalToastProps extends BoxProps {
  dismissable?: boolean;
  variant?: NotificationVariants;
  icon?: IconNameType;
  onRemove?: MouseEventHandler<HTMLDivElement>;
  size?: NotificationSizes;
}

/** UI-Internal Notification Toast display for Internal events (saves/updates/errors) */
export const NotificationInternalToast = styled(
  ({ children, dismissable, variant, icon, size = 'Default', onRemove, ...props }: InternalToastProps) => {
    const theme = useTheme();
    return (
      <NotificationWrapper size={size} data-testid="notification" {...props}>
        {icon != null ? (
          <StatusIcon
            data-testid="notification-status-icon"
            icon={icon}
            color={notificationVariantIconColor(theme, variant)}
            size="fontSizeLarge"
          />
        ) : (
          <IndicatorDot halo variant={fromNotificationVariantToIndicatorDotVariant(variant)} />
        )}
        <Box flex="1">{children}</Box>
        {dismissable && (
          <DismissIcon
            data-testid="notification-close"
            onClick={onRemove}
            icon={IconName.Close}
            color="colors.gray['070']"
            size="fontSizeLarge"
            spaceBefore="auto"
          />
        )}
      </NotificationWrapper>
    );
  }
)``;

const StatusIcon = styled(Icon)`
  flex-shrink: 0;
`;

const DismissIcon = styled(Icon)`
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colorTextDefault};
  }
`;

function notificationVariantIconColor(
  theme: DefaultTheme,
  notification: NotificationVariants = NotificationVariants.Default
): string {
  switch (notification) {
    case NotificationVariants.Negative:
      return theme.colorTextNegative;
    case NotificationVariants.Positive:
      return theme.colorTextPositive;
    case NotificationVariants.Warning:
      return theme.colorTextWarning;
    case NotificationVariants.Primary:
      return theme.colorTextPrimary;
    case NotificationVariants.Attention:
      return theme.colorTextAttention;
    case NotificationVariants.Default:
    default:
      return theme.colorTextDefault;
  }
}

const fromNotificationVariantToIndicatorDotVariant = (
  notification: NotificationVariants = NotificationVariants.Default
): IndicatorDotVariants => {
  switch (notification) {
    case NotificationVariants.Negative:
      return IndicatorDotVariants.Negative;
    case NotificationVariants.Positive:
      return IndicatorDotVariants.Positive;
    case NotificationVariants.Warning:
      return IndicatorDotVariants.Warning;
    case NotificationVariants.Primary:
      return IndicatorDotVariants.Primary;
    case NotificationVariants.Attention:
      return IndicatorDotVariants.Notification;
    case NotificationVariants.Default:
    default:
      return IndicatorDotVariants.Default;
  }
};
